import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router';

import { CloseSessionDto, ReloginAuthDto, ResponseSignInDto, ValidateTokenDto } from '@/dtos';
import { getAdministratorFromLocalStorage } from '@/helpers/utilities';
import authService from '@/services/auth/auth.service';
import accessService from '@/services/auth/access.service';

const routes: Array<RouteRecordRaw> = [
  {
    path: '',
    redirect: {
      name: 'signIn'
    }
  },
  {
    path: '/signin',
    name: 'signIn',
    component: () => import('@/ui/layouts/sign-in/SignInView.vue')
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/ui/layouts/dashboard/DashboardView.vue'),
    children: [
      {
        path: '/', redirect: { name: 'home' }
      },
      {
        path: '/home',
        name: 'home',
        component: () => import('@/ui/pages/home/HomeView.vue')
      },
      {
        path: '/profile/:idAdministrator',
        name: 'profile',
        component: () => import('@/ui/pages/auth/profile/ProfileView.vue'),
        props: true
      },
      {
        path: '/administrators',
        name: 'administrators',
        component: () => import('@/ui/pages/auth/administrators/views/administrador-list/AdministratorsListView.vue')
      },
      {
        path: '/access',
        name: 'access',
        component: () => import('@/ui/pages/auth/access/views/access-list/AccessListView.vue')
      },
      {
        path: '/administrators/config/:idAdministrator',
        name: 'administrators-config',
        props: true,
        component: () => import('@/ui/pages/auth/administrators/views/administrator-config/AdministratorConfigView.vue'),
        children: [
          {
            path: '/administrators/config/:idAdministrator', redirect: { name: 'administrators-applications' }
          },
          {
            path: '/administrators/applications/:idAdministrator',
            name: 'administrators-applications',
            component: () => import('@/ui/pages/auth/administrators/views/adminstrator-applications/AdministratorApplicationsView.vue'),
            props: true
          },
          {
            path: '/administrators/services/:idAdministrator',
            name: 'administrators-services',
            component: () => import('@/ui/pages/auth/administrators/views/administrator-services/AdministratorServicesView.vue'),
            props: true
          },
          {
            path: '/administrators/companies/:idAdministrator',
            name: 'administrators-companies',
            component: () => import('@/ui/pages/auth/administrators/views/administrator-companies/AdministratorCompaniesView.vue'),
            props: true
          },
          {
            path: '/administrators/rechargers/:idAdministrator',
            name: 'administrators-rechargers',
            component: () => import('@/ui/pages/auth/administrators/views/administrator-rechargers/AdministratorRechargerView.vue'),
            props: true
          },
          {
            path: '/administrators/projects/:idAdministrator',
            name: 'administrators-projects',
            component: () => import('@/ui/pages/auth/administrators/views/administrator-project/AdministratorProjectView.vue'),
            props: true
          },
        ]
      },
      {
        path: '/countries',
        name: 'countries',
        component: () => import('@/ui/pages/fleet-management/countries/views/country-list/CountryListView.vue')
      },
      {
        path: '/cities',
        name: 'cities',
        component: () => import('@/ui/pages/fleet-management/cities/views/city-list/CityListView.vue')
      },
      {
        path: '/places',
        name: 'places',
        component: () => import('@/ui/pages/routes/places/views/place-list/PlaceListView.vue')
      },
      {
        path: '/companies',
        name: 'companies',
        component: () => import('@/ui/pages/fleet-management/companies/views/company-list/CompaniesListView.vue')
      },
      {
        path: '/vehicles',
        name: 'vehicles',
        component: () => import('@/ui/pages/fleet-management/vehicles/view/vehicle-list/VehiclesListView.vue')
      },
      {
        path: '/devices',
        name: 'devices',
        component: () => import('@/ui/pages/fleet-management/devices/view/device-list/DevicesListView.vue')
      },
      {
        path: '/chips',
        name: 'chips',
        component: () => import('@/ui/pages/fleet-management/chips/views/chip-list/ChipListView.vue')
      },
      {
        path: '/events',
        name: 'events',
        component: () => import('@/ui/pages/fleet-management/events/view/event-list/EventListView.vue')
      },
      {
        path: '/connection-medium/:idVehicle/:vehicle',
        name: 'connection-medium-id',
        component: () => import('@/ui/pages/fleet-management/connection-medium/view/ConnectionMediumListView.vue'),
        props: true
      },
      {
        path: '/vehicle-application-version',
        name: 'vehicle-application-version',
        component: () => import('@/ui/pages/fleet-management/vehicle-application-version/views/vehicle-application-version-list/VehicleApplicationVersionListView.vue')
      },
      {
        path: '/alert-administrator-vehicle',
        name: 'alert-administrator-vehicle',
        component: () => import('@/ui/pages/fleet-management/alert-administrator-vehicle/views/alert-administrator-vehicle-list/AlertAdministratorVehicleView.vue')
      },
      {
        path: '/routes',
        name: 'routes',
        component: () => import('@/ui/pages/routes/routes/views/route-list/RouteListView.vue')
      },
      {
        path: '/trip-patterns/:idRoute/:route',
        name: 'trip-patterns',
        component: () => import('@/ui/pages/routes/routes/views/trip-pattern-list/TripPatternListView.vue'),
        props: true
      },
      {
        path: '/trip-patterns-checkpoints/:idRoute/:route/:idTripPattern/:tripPattern/:lat/:lon/:zoom/:isValid',
        name: 'trip-patterns-checkpoints',
        component: () => import('@/ui/pages/routes/routes/views/trip-pattern-checkpoint-list/TripPatternCheckpointList.vue'),
        props: true
      },
      {
        path: '/trip-patterns-stops/:idRoute/:route/:idTripPattern/:tripPattern/:lat/:lon/:zoom/:isValid',
        name: 'trip-patterns-stops',
        component: () => import('@/ui/pages/routes/routes/views/trip-pattern-stop-list/TripPatternStopListView.vue'),
        props: true
      },
      {
        path: '/trip-patterns-sectors/:idRoute/:route/:idTripPattern/:tripPattern/:lat/:lon/:zoom/:isValid',
        name: 'trip-patterns-sectors',
        component: () => import('@/ui/pages/routes/routes/views/trip-pattern-sector-list/TripPatternSectorList.vue'),
        props: true
      },
      {
        path: '/checkpoints',
        name: 'checkpoints',
        component: () => import('@/ui/pages/routes/checkpoints/views/checkpoint-list/CheckpointListView.vue')
      },
      {
        path: '/itineraries',
        name: 'itineraries',
        component: () => import('@/ui/pages/routes/itineraries/views/itinerary-list/ItineraryListView.vue')
      },
      {
        path: '/stops',
        name: 'stops',
        component: () => import('@/ui/pages/routes/stops/views/stop-list/StopListView.vue')
      },
      {
        path: '/connection-medium-stop/:idStop/:stop',
        name: 'connection-medium-stop',
        component: () => import('@/ui/pages/fleet-management/connection-medium/view/ConnectionMediumListView.vue'),
        props: true
      },
      {
        path: '/sectors',
        name: 'sectors',
        component: () => import('@/ui/pages/routes/sectors/views/sector-list/SectorListView.vue')
      },
      {
        path: '/dispatchs',
        name: 'dispatchs',
        component: () => import('@/ui/pages/dispatch/dispatch/views/dispatch-list/DispatchListView.vue')
      },
      {
        path: '/vehicle-distribution',
        name: 'vehicle-distribution',
        component: () => import('@/ui/pages/dispatch/vehicle-distribution/views/vehicle-distribution-list/VehicleDistributionListView.vue')
      },
      {
        path: '/report-dispatch',
        name: 'report-dispatch',
        component: () => import('@/ui/pages/dispatch/report-dispatch/views/report-dispatch-view/ReportDispatchView.vue')
      },
      {
        path: '/report-alert-vehicle',
        name: 'report-alert-vehicleh',
        component: () => import('@/ui/pages/tracking/alert-vehicle/views/AlertVehicleReportView.vue')
      },

      {
        path: '/systems',
        name: 'systems',
        component: () => import('@/ui/pages/passengers/system/views/system-list/SystemListView.vue')
      },
      {
        path: '/rechargers',
        name: 'rechargers',
        component: () => import('@/ui/pages/passengers/rechargers/views/recharger-list/RechargerListView.vue')
      },
      {
        path: '/device-recharges',
        name: 'device-recharges',
        component: () => import('@/ui/pages/passengers/device-recharge/views/device-recharge-list/DeviceRechargeListView.vue')
      },
      {
        path: '/recharger-transactions',
        name: 'recharger-transactions',
        component: () => import('@/ui/pages/passengers/recharger-transaction/views/RechargerTransactionReportView.vue')
      },
      {
        path: '/passengers',
        name: 'passengers',
        component: () => import('@/ui/pages/passengers/passenger/views/passenger-list/PassengerListView.vue')
      },
      {
        path: '/payment-method',
        name: 'payment-method',
        component: () => import('@/ui/pages/passengers/payment-methods/views/payment-method-list/PaymentMethodListView.vue')
      },
      {
        path: '/black-list',
        name: 'black-list',
        component: () => import('@/ui/pages/passengers/black-list/views/BlackListView.vue')
      },
      {
        path: '/transactions',
        name: 'transactions',
        component: () => import('@/ui/pages/passengers/trip-ticket/views/transaction-report/TransactionReportView.vue')
      },
      {
        path: '/dispatch-passengers',
        name: 'dispatch-passengers',
        component: () => import('@/ui/pages/passengers/dispatch-passenger/views/dispatch-passenger-list/DispatchPassengerListView.vue')
      },
      {
        path: '/sector-fare',
        name: 'sector-fare',
        component: () => import('@/ui/pages/passengers/sector-fare/views/sector-fare-list/SectorFareListView.vue')
      },
      {
        path: '/report-passenger-count',
        name: 'report-passenger-count',
        component: () => import('@/ui/pages/tracking/passenger-count/views/passenger-count-report/PassengerCountReportView.vue')
      },
      {
        path: '/report-trip-ticket',
        name: 'report-trip-ticket',
        component: () => import('@/ui/pages/passengers/trip-ticket/views/trip-ticket-report/TripTicketReportView.vue')
      },
      {
        path: '/report-download-vehicle-collection',
        name: 'report-download-vehicle-collection',
        component: () => import('@/ui/pages/passengers/download-vehicle-collection/views/report/DownloadVehicleCollectionView.vue')
      },
      {
        path: '/report-sector-fare',
        name: 'report-sector-fare',
        component: () => import('@/ui/pages/passengers/trip-ticket/views/sector-fare-report/SectorFareReportView.vue')
      },
      {
        path: '/project',
        name: 'project',
        component: () => import('@/ui/pages/support/project/views/ProjectListView.vue')
      },
      {
        path: '/resume',
        name: 'resume',
        component: () => import('@/ui/pages/support/resume/views/ResumeSupportView.vue')
      },
      {
        path: '/planification',
        name: 'planification',
        component: () => import('@/ui/pages/support/planification/views/PlanificationSupportView.vue')
      },
      {
        path: '/board',
        name: 'board',
        component: () => import('@/ui/pages/support/board/view/BoardView.vue')
      },
      {
        path: '/project-reports',
        name: 'project-reports',
        component: () => import('@/ui/pages/support/report/views/report-config/ReportConfigView.vue'),
        children: [
          {
            path: '/project-reports', redirect: { name: 'report-incidence-times' }
          },
          {
            path: '/project-reports/incidence-times',
            name: 'report-incidence-times',
            component: () => import('@/ui/pages/support/report/views/report-incidence-times/ReportIncidenceTimeView.vue'),
          },
          {
            path: '/project-reports/incidence-diagram-gantt',
            name: 'report-incidence-diagram-gantt',
            component: () => import('@/ui/pages/support/report/views/report-incidence-diagram-gantt/ReportIncidenceDiagramGanttView.vue'),
          },

        ]
      },
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes
});

router.beforeEach(async (to, from, next) => {
  let alreadyNexted = false;
  const routeName = to.name;
  const administrator = getAdministratorFromLocalStorage();
  const validateTokenDto: ValidateTokenDto = { token: administrator.access_token }

  // En caso de que se acceda al login
  if (routeName == 'signIn') {
    if (!administrator.admin_id) {
      return next();
    }

    try {
      const isValidToken = await authService.validateToken(validateTokenDto);
      if (!isValidToken.data) {
        const reloginAuthDto: ReloginAuthDto = {
          token: administrator.access_token,
          access_id: administrator.access_id,
          application_id: 1
        }

        const newToken = await authService.generateToken(reloginAuthDto);
        const newAdministrator: ResponseSignInDto = {
          ...administrator,
          access_token: newToken.data.access_token
        }

        localStorage.removeItem('administrator');
        localStorage.setItem('administrator', JSON.stringify(newAdministrator));
      }

      alreadyNexted = true;
      return next({ name: 'home' });
    } catch (error) {
      console.log(error);
      localStorage.removeItem('administrator');
      return next({ name: 'signIn' });
    }
  }

  // En caso de que se acceda a otra página
  if (administrator.access_id) {
    if (routeName == 'home') {
      const isValidToken = await authService.validateToken(validateTokenDto);
      if (!isValidToken.data) {
        localStorage.removeItem('administrator');
       
        const closeDto: CloseSessionDto = {
          access_id: administrator.access_id,
          admin_id: administrator.admin_id,
          is_active: false
        }
        accessService.closeSession(closeDto);
       
        return next({ name: 'signIn' });
      }

      return next();
    }

    alreadyNexted = true;
    try {
      const validateTokenDto: ValidateTokenDto = { token: administrator.access_token }
      const isValidToken = await authService.validateToken(validateTokenDto);

      if (!isValidToken.data) {
        alreadyNexted = true;
        try {
          const reloginAuthDto: ReloginAuthDto = {
            token: administrator.access_token,
            access_id: administrator.access_id,
            application_id: 1
          }

          const newToken = await authService.generateToken(reloginAuthDto);
          const newAdministrator: ResponseSignInDto = {
            ...administrator,
            access_token: newToken.data.access_token
          }

          localStorage.removeItem('administrator');
          localStorage.setItem('administrator', JSON.stringify(newAdministrator));
          return next();

        } catch (error) {
          console.log(error);
          localStorage.removeItem('administrator');
          return next({ name: 'signIn' });
        }
      }

      alreadyNexted = true;
      return next();
    } catch (error) {
      console.log(error);
      return next({ name: 'home' });
    }

    alreadyNexted = true;
    return next();
  }

  alreadyNexted = true;
  return next({ name: 'signIn' });
});


export default router;
